import {
  CampaignOverrides,
  CampaignPreviewResponse,
  ComplianceCheckRequest,
  ComplianceResult,
  HubSpotOnboardingInfo,
  TwilioOnboardingConfig,
  TwilioOnboardingListItem,
  TwilioOnboardingStatus,
} from '../types/twilio-onboarding';

const headers = (accessToken: string) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${accessToken}`,
});

export async function fetchOnboardingStatus(
  apiUrl: string,
  accessToken: string,
  clientId: string,
): Promise<TwilioOnboardingStatus | null> {
  const response = await fetch(`${apiUrl}/api/twilio-onboarding/${clientId}`, {
    headers: headers(accessToken),
  });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(
      `Failed to fetch onboarding status: ${await response.text()}`,
    );
  }
  return response.json();
}

export async function startOnboarding(
  apiUrl: string,
  accessToken: string,
  payload: { client_id: number; config: TwilioOnboardingConfig },
): Promise<TwilioOnboardingStatus> {
  const response = await fetch(`${apiUrl}/api/twilio-onboarding/start`, {
    method: 'POST',
    headers: headers(accessToken),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(`Failed to start onboarding: ${await response.text()}`);
  }
  return response.json();
}

export async function runComplianceCheck(
  apiUrl: string,
  accessToken: string,
  params: ComplianceCheckRequest,
): Promise<ComplianceResult> {
  const response = await fetch(
    `${apiUrl}/api/twilio-onboarding/compliance-check`,
    {
      method: 'POST',
      headers: headers(accessToken),
      body: JSON.stringify(params),
    },
  );
  if (!response.ok) {
    throw new Error(`Compliance check failed: ${await response.text()}`);
  }
  return response.json();
}

export async function addInboundNumber(
  apiUrl: string,
  accessToken: string,
  params: { client_id: number; lead_source: string; area_code: string },
): Promise<{ phone_number: string }> {
  const response = await fetch(
    `${apiUrl}/api/twilio-onboarding/inbound-number`,
    {
      method: 'POST',
      headers: headers(accessToken),
      body: JSON.stringify(params),
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to add inbound number: ${await response.text()}`);
  }
  return response.json();
}

export async function updateOnboardingConfig(
  apiUrl: string,
  accessToken: string,
  clientId: string,
  config: Partial<TwilioOnboardingConfig>,
): Promise<TwilioOnboardingConfig> {
  const response = await fetch(
    `${apiUrl}/api/twilio-onboarding/${clientId}/config`,
    {
      method: 'PATCH',
      headers: headers(accessToken),
      body: JSON.stringify(config),
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to update config: ${await response.text()}`);
  }
  return response.json();
}

export async function fetchCampaignPreview(
  apiUrl: string,
  accessToken: string,
  clientId: string,
): Promise<CampaignPreviewResponse> {
  const response = await fetch(
    `${apiUrl}/api/twilio-onboarding/${clientId}/campaign-preview`,
    { headers: headers(accessToken) },
  );
  if (!response.ok) {
    throw new Error(
      `Failed to fetch campaign preview: ${await response.text()}`,
    );
  }
  return response.json();
}

export async function resubmitCampaign(
  apiUrl: string,
  accessToken: string,
  clientId: string,
  overrides?: CampaignOverrides,
): Promise<TwilioOnboardingStatus> {
  const response = await fetch(
    `${apiUrl}/api/twilio-onboarding/${clientId}/resubmit-campaign`,
    {
      method: 'POST',
      headers: headers(accessToken),
      body: JSON.stringify(overrides ?? {}),
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to resubmit campaign: ${await response.text()}`);
  }
  return response.json();
}

export async function uploadScreenshot(
  apiUrl: string,
  accessToken: string,
  clientId: string,
  file: File,
): Promise<{ screenshot_url: string }> {
  const buffer = await file.arrayBuffer();
  const base64 = new Uint8Array(buffer).toBase64();
  const response = await fetch(
    `${apiUrl}/api/twilio-onboarding/${clientId}/upload-screenshot`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ screenshot_base64: base64 }),
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to upload screenshot: ${await response.text()}`);
  }
  return response.json();
}

export async function listOnboardings(
  apiUrl: string,
  accessToken: string,
  statusFilter?: string,
): Promise<TwilioOnboardingListItem[]> {
  const params = statusFilter ? `?status=${statusFilter}` : '';
  const response = await fetch(
    `${apiUrl}/api/twilio-onboarding/list${params}`,
    { headers: headers(accessToken) },
  );
  if (!response.ok) {
    throw new Error(`Failed to list onboardings: ${await response.text()}`);
  }
  return response.json();
}

export async function fetchHubSpotOnboardingInfo(
  apiUrl: string,
  accessToken: string,
  clientId: string,
): Promise<HubSpotOnboardingInfo | null> {
  const response = await fetch(
    `${apiUrl}/api/client/${clientId}/hubspot-onboarding-info`,
    { headers: headers(accessToken) },
  );
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(
      `Failed to fetch HubSpot onboarding info: ${await response.text()}`,
    );
  }
  return response.json();
}
